import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  McNotification,
  McLoadingIndicator,
  McTabBar,
  McTab,
  McTag,
  McButton,
  McIcon,
} from '@maersk-global/mds-react-wrapper';
import { McTooltip } from '@maersk-global/mds-react-wrapper/components-core/mc-tooltip';
import { useQueryDetailController } from '../hooks/useQueryDetailController';
import { QueryDetailHeader } from '../components/query-detail/QueryDetailHeader';
import { QueryListSidebar } from '../components/queries/QueryListSidebar';
import { QueryOverviewTab } from '../components/query-detail/tabs/QueryOverviewTab';
import { QueryVersionsTab } from '../components/query-detail/tabs/QueryVersionsTab';
import { OverrideVersionsTab } from '../components/query-detail/tabs/OverrideVersionsTab';
import { TerminalSelect } from '../components/Common/TerminalSelect';
import { DatabaseSelect } from '../components/Common/DatabaseSelect';
import { ConnectionSelect } from '../components/Common/ConnectionSelect';
import { CreateVersionDialog } from '../components/queries/dialogs/CreateVersionDialog';
import { queryService } from '../services/queryService';
import { SchemaExplorer } from '../components/SchemaExplorer';
import { SchemaExplorerPanel } from '../components/SchemaExplorerPanel';
import { useSchemaPanelState } from '../hooks/useSchemaPanelState';
import { useNotification } from '../context/NotificationContext';

export default function QueryDetailPage() {
  const { queryId } = useParams<{ queryId: string }>();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const querySidebarPush = 'clamp(220px, 24vw, 300px)';
  const { state, handlers } = useQueryDetailController(queryId!);
  const { showNotification } = useNotification();
  const { open: schemaPanelOpen, setOpen: setSchemaPanelOpen, toggle: toggleSchemaPanel } = useSchemaPanelState('overview');
  const [showSchemaExplorerFull, setShowSchemaExplorerFull] = React.useState(false);

  const { data: terminalOverrides = [] } = useQuery({
    queryKey: ['terminalOverrides', queryId],
    queryFn: () => queryService.listTerminalOverrides(queryId!),
    enabled: !!queryId,
    staleTime: 1000 * 60,
  });

  const {
    query,
    isLoading,
    error,
    tabValue,
    setTabValue,
    selectedTerminal,
    setSelectedTerminal,
    selectedDatabase,
    setSelectedDatabase,
    selectedConnection,
    setSelectedConnection,
    executionParameters,
    executionError,
    lastExecutionResult,
    versionHistory,
    isExecuting,
    isToggling,
    
    // Version creation state
    versionDialogOpen,
    loadingVersionNumber,
    dialogError,
    versionForm,
    isCreatingVersion,
    isGeneratingAiExplanation,
    validationResult,
    isExplainingPlan,
    aiExplanation,
    aiModelOptions,
    aiModelName
  } = state;

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <McLoadingIndicator />
      </div>
    );
  }

  if (error || !query) {
    const errorDetail = (error as any)?.response?.data?.detail || (error as any)?.response?.data?.title || error?.message || 'Query not found';
    return (
      <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
        <McNotification
          appearance="error"
          heading="Error"
          body={`Failed to load query details: ${errorDetail}`}
          style={{ width: '100%', maxWidth: '800px' }}
        />
        <McButton onClick={handlers.handleNavigateBack} variant="outlined" appearance="secondary">
          <McIcon icon="arrow-left" slot="prefix" />
          Go Back
        </McButton>
      </div>
    );
  }

  // If the selected terminal has an override, show that SQL in the overview panel.
  // terminalOverrides already contains sqlContent — no extra fetch needed.
  const activeOverride = terminalOverrides.find(o => o.terminalId === selectedTerminal);
  const displaySqlContent = activeOverride ? activeOverride.sqlContent : (query?.sqlContent || '');

  return (
    <>
      <QueryListSidebar
        isOpen={sidebarOpen}
        onSelectQuery={(nextQueryId) => navigate(`/queries/${nextQueryId}`)}
        selectedQueryId={queryId}
      />

      <div style={{
        marginLeft: sidebarOpen ? querySidebarPush : '0px',
        marginTop: '-6px',
        transition: 'margin-left 0.3s ease',
      }}>
        <QueryDetailHeader
          query={{ ...query, category: query.category || 'Uncategorized' }}
          onNavigateEdit={handlers.handleNavigateEdit}
          onNavigateCreateSchedule={handlers.handleNavigateCreateSchedule}
          onToggleActive={handlers.handleToggleActive}
          onDelete={handlers.handleDelete}
          onToggleSidebar={() => setSidebarOpen((open) => !open)}
          isSidebarOpen={sidebarOpen}
          isTogglePending={isToggling}
        />

        {/* ── Toolbar: Tabs (left) + overrides chip (centre) + Run controls (right) ── */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          borderBottom: '1px solid #e5e7eb',
          marginBottom: 0, gap: 10, flexWrap: 'nowrap', overflow: 'visible',
          backgroundColor: '#fff',
        }}>
          <div style={{ flexShrink: 0 }}>
            <McTabBar currentindex={tabValue} tabchange={(e: any) => setTabValue(e.detail)}>
              <McTab slot="tab" label="Overview" />
              <McTab slot="tab" label="Versions" />
              <McTab slot="tab" label="Override Versions" />
            </McTabBar>
          </div>

          {terminalOverrides.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
              <McTooltip
                onMouseOver={(e: any) => { e.currentTarget.open = true; }}
                onMouseOut={(e: any) => { e.currentTarget.open = false; }}
                position="bottom-center"
              >
                <div slot="trigger" style={{ display: 'inline-block', cursor: 'default' }}>
                  <McTag
                    label={`⚡ ${terminalOverrides.length} override${terminalOverrides.length !== 1 ? 's' : ''}`}
                    appearance="warning"
                    fit="small"
                  />
                </div>
                <div  style={{ maxHeight: '200px', overflowY: 'auto', minWidth: '120px' }}>
                  {terminalOverrides.map(o => (
                    <div key={o.terminalId} style={{ padding: '4px 0', fontSize: '12px' }}>{o.terminalId}</div>
                  ))}
                </div>
              </McTooltip>
            </div>
          )}

          {/* Controls visible only on Overview tab */}
          {tabValue === 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0, padding: '4px 8px' }}>
              <div style={{ width: 130 }}>
                <DatabaseSelect
                  label=""
                  placeholder="DB Type"
                  value={selectedDatabase}
                  onChange={(value) => {
                    setSelectedDatabase(value);
                    if (value !== 'EXTERNAL') {
                      setSelectedConnection('');
                    }
                  }}
                  disabled={isExecuting}
                />
              </div>
              {selectedDatabase === 'EXTERNAL' ? (
                <div style={{ width: 260 }}>
                  <ConnectionSelect
                    label=""
                    placeholder="Select Connection"
                    value={selectedConnection}
                    onChange={(id) => setSelectedConnection(id)}
                    disabled={isExecuting}
                  />
                </div>
              ) : (
                <div style={{ width: 220 }}>
                  <TerminalSelect
                    label=""
                    placeholder="Select Terminal"
                    value={selectedTerminal}
                    onChange={setSelectedTerminal}
                    disabled={isExecuting}
                  />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Tab Panels */}
        {tabValue === 0 && (
          <QueryOverviewTab
            query={{ queryId: query?.queryId || '' }}
            onToggleSchemaPanel={toggleSchemaPanel}
            schemaPanelOpen={schemaPanelOpen}
            sqlContent={displaySqlContent}
            baseSqlContent={activeOverride ? (query?.sqlContent || '') : undefined}
            isOverride={!!activeOverride}
            terminalId={selectedTerminal || undefined}
            onCopy={handlers.handleCopyToClipboard}
            executionParameters={executionParameters}
            onParameterChange={handlers.handleParameterChange}
            isExecuting={isExecuting}
            executionResult={lastExecutionResult}
            executionError={executionError}
            planLines={validationResult?.planLines || []}
            isExplainingPlan={isExplainingPlan}
            aiExplanation={aiExplanation}
            isGeneratingAiExplanation={isGeneratingAiExplanation}
             validationErrors={[
              ...(validationResult?.syntaxErrors || []),
              ...(validationResult?.vulnerabilities?.map((v: any) => v.description) || [])
            ]}
            onAiExplain={handlers.handleAiExplainPlan}
            onCloseExplainPlan={handlers.handleCloseExplainPlan}
            aiModelOptions={aiModelOptions}
            selectedModel={aiModelName}
            onModelChange={handlers.setAiModelName}
            onExecute={handlers.handleExecute}
            onExplain={handlers.handleExplainPlan}
            canExecute={!isExecuting && (selectedDatabase === 'EXTERNAL' ? !!selectedConnection : !!selectedTerminal)}
            canExplain={!isExecuting && !isExplainingPlan && (selectedDatabase === 'EXTERNAL' ? !!selectedConnection : !!selectedTerminal)}
          />
        )}
        {tabValue === 1 && (
          <QueryVersionsTab 
            versions={versionHistory || []} 
            currentVersion={query.currentVersion} 
            onSetCurrentVersion={handlers.handleSetCurrentVersion} 
            isSettingCurrentVersion={state.isSettingCurrentVersion}

          />
        )}
        {tabValue === 2 && (
          <OverrideVersionsTab queryId={queryId!} terminalOverrides={terminalOverrides} />
        )}

        {/* Dialogs */}
        <CreateVersionDialog
          open={versionDialogOpen}
          onClose={handlers.handleVersionDialogClose}
          selectedQuery={query}
          schedules={state.schedules || []}
          dialogError={dialogError}
          loadingVersionNumber={loadingVersionNumber}
          versionForm={versionForm}
          setVersionForm={handlers.setVersionForm}
          onSubmit={handlers.handleVersionSubmit}
          isPending={isCreatingVersion}
        />

        <SchemaExplorer
          open={showSchemaExplorerFull}
          onClose={() => setShowSchemaExplorerFull(false)}
          terminalId={selectedTerminal}
          databaseType={selectedDatabase}
        />

        <SchemaExplorerPanel
          open={schemaPanelOpen}
          onOpenChange={setSchemaPanelOpen}
          terminalId={selectedTerminal}
          databaseType={selectedDatabase}
          variant="overview"
          onOpenFullscreen={() => setShowSchemaExplorerFull(true)}
          onEditQuery={() => navigate(`/queries/${queryId}/edit`)}
          onInsertSnippet={(text) => {
            navigator.clipboard.writeText(text);
            showNotification(`Copied ${text}`, 'success');
          }}
        />
      </div>
    </>
  );
}
